import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { getSubscription, putSubscription } from '../../../api/items';

import {
  checkAndSetLoadingWithId,
  handleRequestRejection,
  onMatchedRequest,
} from '../../../utils/asyncThunkRequest';
import { fetchItem } from './fetchItem';

import { generateSubscriptionCIDKey } from '../../../utils/generateSubscriptionCIDKey';
import { generateChannelCIDKey } from '../../../utils/generateChannelCIDKey';

import { REQUEST } from '../../../constants/request';

export const updateSubscription = createAsyncThunk(
  'channels/updateSubscription',
  async (subscriptionData, { signal, rejectWithValue, dispatch }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });

    try {
      const response = await putSubscription(subscriptionData, {
        cancelToken: source.token,
      });

      const itemResponse = await dispatch(fetchItem({ path: response.data }));

      if (itemResponse.type === `${fetchItem.typePrefix}/rejected`) {
        return rejectWithValue("Couldn't fetch item");
      }

      const subscriptionResponse = await getSubscription(
        {
          path: itemResponse.payload.content.properties.gmantra.subscription,
        },
        {
          cancelToken: source.token,
        },
      );

      return {
        subscription: subscriptionResponse.data,
        itemCID: response.data,
      };
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
  {
    condition: ({ path }, { getState }) => {
      const state = getState();

      return !(
        state.items.subscriptions.actions.update.request[
          generateChannelCIDKey(path)
        ]?.loading === REQUEST.pending
      );
    },
  },
);

export const updateSubscriptionExtraReducers = builder => {
  builder
    .addCase(updateSubscription.pending, (state, action) => {
      checkAndSetLoadingWithId(
        state.subscriptions.actions.update.request,
        action,
        generateChannelCIDKey(action.meta.arg.path),
      );
    })
    .addCase(updateSubscription.fulfilled, (state, action) => {
      const channelCIDKey = generateChannelCIDKey(action.meta.arg.path);

      onMatchedRequest(
        state.subscriptions.actions.update.request[channelCIDKey],
        action,
        () => {
          const subscriptionCIDKey = generateSubscriptionCIDKey({
            ...action.payload.subscription.channelCID,
            subscriptionId: action.payload.subscription.id,
          });

          state.subscriptions.byId[subscriptionCIDKey] = action.payload.payload;

          if (!state.subscriptions.allIds.includes(subscriptionCIDKey)) {
            state.subscriptions.allIds.push(subscriptionCIDKey);
          }
        },
      );
    })
    .addCase(updateSubscription.rejected, (state, action) => {
      handleRequestRejection(
        state.subscriptions.actions.update.request[
          generateChannelCIDKey(action.meta.arg.path)
        ],
        action,
      );
    });
};
